/**
 * Centralized Cache Invalidation Strategy
 * Defines which caches should be invalidated for each booking operation
 */

import { queryKeys } from "../api/queryKeys.js";

/**
 * Cache invalidation strategy for different booking operations
 * Each operation defines:
 * - required: Always invalidated queries
 * - conditional: Queries invalidated based on conditions
 * - optional: Queries that may benefit from invalidation but aren't critical
 */
export const cacheStrategy = {
	/**
	 * Create booking (single instance or series)
	 */
	create: {
		required: (data) => [
			// Always invalidate the affected room's bookings
			queryKeys.bookingsByRoom(data.roomId),
			// Always invalidate all bookings for calendar views
			queryKeys.allBookings(),
			// Always invalidate user's personal bookings
			queryKeys.myBookings(),
			// Always invalidate user's transaction data (points may be consumed)
			queryKeys.myTransactions(),
		],
		conditional: (data) => {
			const queries = [];

			// If creating a series, invalidate series queries
			if (data.endpoint === "series/create" || data.data?.recurrence) {
				queries.push(queryKeys.series());
			}

			return queries;
		},
		optional: () => [
			// Master data might be affected (room availability)
			queryKeys.masterData(),
			// Admin logs should reflect new booking
			queryKeys.adminLogs(),
		],
	},

	/**
	 * Update booking (single instance). Also covers editing a single occurrence of a
	 * series (which detaches it) — both "update" and "update-instance" actions route
	 * through this same strategy, since they share one mutation (useUpdateBookingMutation).
	 */
	update: {
		required: (data) => [
			// Always invalidate affected room's bookings
			queryKeys.bookingsByRoom(data.roomId),
			// Always invalidate all bookings
			queryKeys.allBookings(),
			// Always invalidate user's personal bookings
			queryKeys.myBookings(),
			// Always invalidate user's transaction data (points may change)
			queryKeys.myTransactions(),
		],
		conditional: (data) => {
			const queries = [];

			// If room changed, invalidate old room's cache too
			if (data.originalRoomId && data.originalRoomId !== data.roomId) {
				queries.push(queryKeys.bookingsByRoom(data.originalRoomId));
			}

			// If booking ID is available, invalidate its logs
			if (data.bookingId) {
				queries.push(queryKeys.bookingLogs(data.bookingId));
			}

			// If this occurrence belonged to a series, editing it just detached it and
			// wrote a series-level log entry too (the series' excluded-dates list grew) —
			// that entry is visible from every other occurrence's log view, not just this
			// one, so broadly invalidate rather than trying to enumerate sibling IDs.
			if (data.seriesId) {
				queries.push(queryKeys.series());
				queries.push(queryKeys.seriesById(data.seriesId));
				queries.push(queryKeys.allBookingLogs());
			}

			return queries;
		},
		optional: () => [queryKeys.masterData(), queryKeys.adminLogs()],
	},

	/**
	 * Update series
	 */
	updateSeries: {
		required: (data) => [
			queryKeys.bookingsByRoom(data.roomId),
			queryKeys.allBookings(),
			queryKeys.myBookings(),
			// Always invalidate user's transaction data (points may change)
			queryKeys.myTransactions(),
			// Always invalidate series queries for series operations
			queryKeys.series(),
			queryKeys.seriesById(data.seriesId),
		],
		conditional: (data) => {
			const queries = [];

			if (data.originalRoomId && data.originalRoomId !== data.roomId) {
				queries.push(queryKeys.bookingsByRoom(data.originalRoomId));
			}

			// A series update writes a single series-level log entry that's visible from
			// every occurrence's log view (including ones dropped by a shrinking pattern),
			// not just a specific booking — invalidate every cached per-booking log query
			// rather than trying to enumerate exactly which booking IDs are affected.
			if (data.seriesId) {
				queries.push(queryKeys.allBookingLogs());
			}

			return queries;
		},
		optional: () => [queryKeys.masterData(), queryKeys.adminLogs()],
	},

	/**
	 * Delete booking (single instance)
	 */
	delete: {
		required: (data) => [
			queryKeys.allBookings(),
			queryKeys.myBookings(),
			// Always invalidate user's transaction data (points are returned)
			queryKeys.myTransactions(),
			// Always invalidate room bookings since a booking was deleted
			queryKeys.bookingsByRoom(data.roomId),
		],
		conditional: (data) => {
			const queries = [];

			// If booking ID is available, invalidate its logs
			if (data.bookingId) {
				queries.push(queryKeys.bookingLogs(data.bookingId));
			}

			return queries;
		},
		optional: () => [queryKeys.masterData(), queryKeys.adminLogs()],
	},

	/**
	 * Delete series
	 */
	deleteSeries: {
		required: (data) => [
			queryKeys.allBookings(),
			queryKeys.myBookings(),
			// Always invalidate user's transaction data (points are returned)
			queryKeys.myTransactions(),
			queryKeys.series(),
			// Always invalidate room bookings since series was deleted
			queryKeys.bookingsByRoom(data.roomId),
		],
		conditional: (data) => {
			const queries = [];

			if (data.seriesId) {
				queries.push(queryKeys.seriesById(data.seriesId));
				// cancelling the whole series writes a series-level log entry visible from
				// every occurrence's log view — see updateSeries for the same reasoning
				queries.push(queryKeys.allBookingLogs());
			}

			return queries;
		},
		optional: () => [queryKeys.masterData(), queryKeys.adminLogs()],
	},

	/**
	 * Delete instance (single occurrence from series)
	 */
	deleteInstance: {
		required: (data) => [
			queryKeys.allBookings(),
			queryKeys.myBookings(),
			// Always invalidate user's transaction data (points are returned)
			queryKeys.myTransactions(),
		],
		conditional: (data) => {
			const queries = [];

			if (data.roomId) {
				queries.push(queryKeys.bookingsByRoom(data.roomId));
			}

			// Always invalidate series queries when deleting from series
			if (data.seriesId) {
				queries.push(queryKeys.series());
				queries.push(queryKeys.seriesById(data.seriesId));
				// cancelling one occurrence still writes a series-level log entry (the
				// updated cancellation date list) visible from every sibling occurrence's
				// log view — see updateSeries for the same reasoning
				queries.push(queryKeys.allBookingLogs());
			}

			// If booking ID is available, invalidate its logs
			if (data.bookingId) {
				queries.push(queryKeys.bookingLogs(data.bookingId));
			}

			return queries;
		},
		optional: () => [queryKeys.masterData(), queryKeys.adminLogs()],
	},
};

/**
 * Get all queries that should be invalidated for a given operation
 * @param {string} operation - The operation type (create, update, delete, etc.)
 * @param {Object} data - Operation data for conditional invalidations
 * @param {Object} options - Additional options
 * @param {boolean} options.includeOptional - Whether to include optional invalidations
 * @returns {Array} Array of query keys to invalidate
 */
export function getInvalidationQueries(operation, data = {}, options = {}) {
	const { includeOptional = true } = options;
	const strategy = cacheStrategy[operation];

	if (!strategy) {
		console.warn(`No cache strategy defined for operation: ${operation}`);
		return [];
	}

	const queries = [...strategy.required(data), ...strategy.conditional(data)];

	if (includeOptional) {
		queries.push(...strategy.optional(data));
	}

	// Remove duplicates
	return [...new Set(queries.map((query) => JSON.stringify(query)))].map(
		(query) => JSON.parse(query)
	);
}

/**
 * Invalidate queries for a specific operation
 * @param {Object} queryClient - TanStack Query client
 * @param {string} operation - Operation type
 * @param {Object} data - Operation data
 * @param {Object} options - Additional options
 */
export function invalidateQueriesForOperation(
	queryClient,
	operation,
	data = {},
	options = {}
) {
	const queries = getInvalidationQueries(operation, data, options);

	queries.forEach((queryKey) => {
		queryClient.invalidateQueries({ queryKey });
	});
}

/**
 * Helper to extract room ID from various data structures
 * @param {Object} data - Operation data
 * @returns {number|null} Room ID if found
 */
export function extractRoomId(data) {
	return data.roomId || data.bookingData?.roomId || data.data?.roomId || null;
}

/**
 * Helper to extract series ID from various data structures
 * @param {Object} data - Operation data
 * @returns {number|null} Series ID if found
 */
export function extractSeriesId(data) {
	return data.seriesId || data.bookingData?.seriesId || data.series?.id || null;
}
